import type { AutocompleteData } from "@color-finder/color-finder-api/models";
import CircleSharpIcon from "@mui/icons-material/CircleSharp";
import {
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import type { Dispatch, FC } from "react";
import { useTranslation } from "react-i18next";
import { getColorGroupValues } from "../../../service/autocomplete-lookup";

export interface ColorGroupingFilterProps {
  value?: Array<AutocompleteData> | undefined;
  onChange?: Dispatch<Array<AutocompleteData>>;
}

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  flexWrap: "wrap",
  margin: `0 ${theme.spacing(-0.5)} ${theme.spacing(0)}`,
  "& .MuiToggleButtonGroup-grouped": {
    margin: theme.spacing(0),
    borderRadius: "50%",
    border: "none",
    padding: theme.spacing(0.5),
    "&.Mui-disabled": {
      border: 0,
    },
    "&:not(:first-of-type)": {
      borderRadius: "50%",
    },
    "&:first-of-type": {
      borderRadius: "50%",
    },
  },
}));

export const ColorGroupingFilter: FC<ColorGroupingFilterProps> = ({
  value = [],
  onChange,
}) => {
  const { t } = useTranslation();
  const colorOptions = getColorGroupValues();
  const onColorToggle = (color: AutocompleteData) => {
    if (value.some((c) => c.value === color.value))
      onChange?.(value.filter((c) => c.value !== color.value));
    else onChange?.([...value, color]);
  };

  return (
    <Stack paddingTop={"14px"} alignItems="start" rowGap={0.5}>
      <Typography
        color="text.primary"
        mb={"16px"}
        fontSize="var(--body-b2-14-regular-size, 14px)"
        fontFamily='var(--font-family, "AktivGrotesk")'
        fontWeight={700}
      >
        {t("filter_color_group_title")}
      </Typography>
      <Typography
        color="text.secondary"
        fontSize="var(--body-b3-12-regular-size, 12px)"
        letterSpacing="0.125px"
      >
        {t("filter_color_group_subtitle")}
      </Typography>
      <StyledToggleButtonGroup>
        {colorOptions.map((color) => {
          const isActive = value.some((c) => c.value === color.value);
          return (
            <ToggleButton
              key={color.name}
              title={color.name}
              value={color.name}
              onClick={() => onColorToggle(color)}
            >
              <CircleSharpIcon
                sx={{
                  color: color.color,
                  border: (theme) =>
                    isActive
                      ? `3px solid ${theme.palette.grey["400"]}`
                      : undefined,
                  borderRadius: "50%",
                  fontSize: 34,
                  opacity: !value.length || isActive ? 1 : 0.3,
                }}
              />
            </ToggleButton>
          );
        })}
      </StyledToggleButtonGroup>
    </Stack>
  );
};
